import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Car, Phone, Search, UserRound } from 'lucide-react';
import { publicApi } from '../lib/api';
import { BrandMark } from '../components/BrandMark';

type Role = 'driver' | 'passenger';

type EventLite = {
  id: string;
  name: string;
  event_date: string | null;
  event_time: string | null;
  venue_name: string | null;
  venue_address: string | null;
};

type Registration = {
  id: string;
  status: string;
  origin: string;
  direction: string;
  notes: string | null;
  seats_total?: number;
  seats_needed?: number;
  driver_name?: string;
  passenger_name?: string;
};

type DriverMatch = {
  driver_name: string;
  driver_phone: string;
  origin: string;
  departure_time: string | null;
  direction: string;
} | null;
type PassengerMatch = Array<{
  passenger_name: string;
  passenger_phone: string;
  origin: string;
  seats_needed: number;
  status: string;
}>;

type ManageData = {
  role: Role;
  event: EventLite | null;
  registration: Registration;
  match: DriverMatch | PassengerMatch;
};

const STATUS_LABEL: Record<string, string> = {
  open: 'פעיל',
  pending: 'ממתין לשיבוץ',
  matched: 'שובץ',
  full: 'מלא',
  cancelled: 'בוטל',
  completed: 'הושלם',
};

const DIRECTION_LABEL: Record<string, string> = {
  to_event: 'לאירוע',
  from_event: 'מהאירוע',
  round_trip: 'הלוך-חזור',
};

export default function ManageRidePage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<ManageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [editing, setEditing] = useState(false);
  const [origin, setOrigin] = useState('');
  const [seats, setSeats] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  function load() {
    setLoading(true);
    publicApi
      .get(`/public/manage/${token}`)
      .then((r) => {
        setData(r.data);
        setOrigin(r.data.registration.origin || '');
        setSeats(String(r.data.registration.seats_total ?? r.data.registration.seats_needed ?? ''));
        setNotes(r.data.registration.notes || '');
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }

  useEffect(load, [token]);

  async function save() {
    setBusy(true);
    setMsg('');
    try {
      await publicApi.patch(`/public/manage/${token}`, {
        origin: origin.trim(),
        seats: seats ? parseInt(seats, 10) : undefined,
        notes: notes.trim() || null,
      });
      setEditing(false);
      setMsg('הפרטים עודכנו');
      load();
    } catch {
      setMsg('שגיאה בעדכון');
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!window.confirm('לבטל את ההרשמה לטרמפ?')) return;
    setBusy(true);
    try {
      await publicApi.delete(`/public/manage/${token}`);
      setMsg('ההרשמה בוטלה');
      load();
    } catch {
      setMsg('שגיאה בביטול');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Centered>
        <div className="h-10 w-10 rounded-2xl bg-sage-600/80 animate-pulse" />
      </Centered>
    );
  }

  if (notFound || !data) {
    return (
      <Centered>
        <div className="text-center page-enter">
          <div className="mx-auto mb-4 grid place-items-center w-14 h-14 rounded-2xl bg-paper-mist text-ink-mute">
            <Search size={24} />
          </div>
          <h1 className="font-display text-2xl font-bold text-ink">ההרשמה לא נמצאה</h1>
        </div>
      </Centered>
    );
  }

  const { role, event, registration, match } = data;
  const isCancelled = registration.status === 'cancelled';
  const name = registration.driver_name || registration.passenger_name || '';

  return (
    <div dir="rtl" className="min-h-full px-4 py-10 flex flex-col items-center bg-paper">
      <div className="w-full max-w-md space-y-4 page-enter">
        <header className="text-center mb-2">
          <div className="flex justify-center mb-4">
            <BrandMark size="sm" showTagline={false} />
          </div>
          <div className="mx-auto mb-4 grid place-items-center w-12 h-12 rounded-full bg-sage-100 text-sage-700">
            {role === 'driver' ? <Car size={22} aria-hidden /> : <UserRound size={22} aria-hidden />}
          </div>
          <h1 className="font-sans text-2xl font-bold text-ink">
            {role === 'driver' ? 'הנסיעה שלי' : 'הטרמפ שלי'}
          </h1>
          {event && <p className="text-ink-mute text-sm mt-1">ל{event.name}</p>}
        </header>

        {msg && (
          <p className="text-center text-sm text-sage-700 bg-sage-50 border border-sage-100 rounded-xl px-3 py-2.5">
            {msg}
          </p>
        )}

        <section className="surface p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <span className="font-bold text-ink">{name}</span>
            <span
              className={`chip ${
                isCancelled ? 'bg-paper-mist text-ink-faint' : 'bg-sage-50 text-sage-700'
              }`}
            >
              {STATUS_LABEL[registration.status] || registration.status}
            </span>
          </div>

          {!editing ? (
            <div className="space-y-2.5 text-sm">
              <Row label="יציאה מ">{registration.origin}</Row>
              <Row label="כיוון">{DIRECTION_LABEL[registration.direction] || registration.direction}</Row>
              <Row label={role === 'driver' ? 'מקומות פנויים' : 'מקומות'}>
                {String(registration.seats_total ?? registration.seats_needed ?? '')}
              </Row>
              {registration.notes && <Row label="הערות">{registration.notes}</Row>}
            </div>
          ) : (
            <div className="space-y-3">
              <input
                className="field"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                placeholder="נקודת יציאה"
                aria-label="נקודת יציאה"
              />
              <input
                className="field"
                value={seats}
                onChange={(e) => setSeats(e.target.value)}
                placeholder="מקומות"
                inputMode="numeric"
                aria-label="מקומות"
              />
              <textarea
                className="field resize-none"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="הערות"
                aria-label="הערות"
              />
            </div>
          )}

          {!isCancelled && (
            <div className="flex gap-2 pt-1">
              {!editing ? (
                <>
                  <button type="button" onClick={() => setEditing(true)} className="btn-primary flex-1">
                    עריכה
                  </button>
                  <button type="button" onClick={cancel} disabled={busy} className="btn-danger flex-1">
                    ביטול הרשמה
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={save} disabled={busy} className="btn-primary flex-1">
                    {busy ? 'שומר…' : 'שמירה'}
                  </button>
                  <button type="button" onClick={() => setEditing(false)} className="btn-ghost flex-1">
                    ביטול
                  </button>
                </>
              )}
            </div>
          )}
        </section>

        {role === 'passenger' && match && !Array.isArray(match) && (
          <section className="surface p-5">
            <h2 className="font-display font-bold text-ink mb-3 flex items-center gap-2">
              <Car size={18} className="text-sage-600" />
              שובצתם לנהג
            </h2>
            <div className="space-y-2.5 text-sm">
              <Row label="נהג/ת">{match.driver_name}</Row>
              <Row label="טלפון">
                <a
                  href={`tel:${match.driver_phone}`}
                  className="inline-flex items-center gap-1 text-sage-600 font-semibold hover:underline cursor-pointer"
                >
                  <Phone size={13} />
                  {match.driver_phone}
                </a>
              </Row>
              <Row label="יציאה מ">{match.origin}</Row>
            </div>
          </section>
        )}

        {role === 'driver' && Array.isArray(match) && match.length > 0 && (
          <section className="surface p-5">
            <h2 className="font-display font-bold text-ink mb-3">
              הנוסעים שלך ({match.length})
            </h2>
            <div className="space-y-3">
              {match.map((p, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-sm border-b border-line last:border-0 pb-3 last:pb-0"
                >
                  <div>
                    <div className="font-semibold text-ink">{p.passenger_name}</div>
                    <div className="text-ink-faint text-xs mt-0.5">{p.origin}</div>
                  </div>
                  <a
                    href={`tel:${p.passenger_phone}`}
                    className="inline-flex items-center gap-1 text-sage-600 font-semibold hover:underline cursor-pointer"
                  >
                    <Phone size={13} />
                    {p.passenger_phone}
                  </a>
                </div>
              ))}
            </div>
          </section>
        )}

        <p className="text-center text-[11px] text-ink-faint tracking-wide pt-2">Powered by DoWe</p>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-faint">{label}</span>
      <span className="font-medium text-ink text-left">{children}</span>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div dir="rtl" className="min-h-full grid place-items-center px-4">
      {children}
    </div>
  );
}
